import {
  ArrowNarrowUpRightIcon,
  File05Icon,
} from '@langchain/untitled-ui-icons';
import { Modal, ModalClose, ModalDialog } from '@mui/joy';

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { TextDisplayMode } from '@/Pages/DatasetSessionCompare/constants';
import { DatasetAndSplitPicker } from '@/Pages/Playground/components/DatasetAndSplitPicker';
import { CircularProgress } from '@/components/CircularProgress';
import useToast from '@/components/Toast/useToast';
import { Button, IconButton, Tooltip } from '@/design-system';
import { useExamples, useOrganizationId } from '@/hooks/useSwr';
import { ExampleSchema } from '@/types/schema';
import {
  appComparePath,
  appDatasetsPath,
  appOrganizationPath,
} from '@/utils/constants';
import { xCount } from '@/utils/stringUtils';
import { cn } from '@/utils/tailwind';

import { useGraphAssistant, useLangGraphClient } from '../../api';
import { useGraphServerInfo } from '../../api/useGraphSwr';
import { useDatasetIsCompatibleWithSchema } from '../../hooks/useDatasetIsCompatibleWithSchema';
import { useRunStudioExperiment } from '../../hooks/useRunStudioExperiment';
import { useAssistantStore } from '../../store/assistants/assistantsStore';
import { isHostedUrl } from '../../utils';
import { checkIsSystemAssistant } from './Assistants/utils';

const EXPERIMENT_PREFIX = 'studio';

const ExperimentButton = ({ onClick }: { onClick: () => void }) => {
  const [_, studioUrl] = useLangGraphClient();
  const { data: studioInfo } = useGraphServerInfo();
  const { langsmith_tracing_replicas: serverSupportsExperiments, langsmith } =
    studioInfo?.flags ?? {};
  const isHosted = isHostedUrl(studioUrl);

  const disabledMessage = (() => {
    if (!langsmith)
      return 'Enable tracing via the LANGSMITH_API_KEY environment variable to run an experiment';
    if (serverSupportsExperiments) return undefined;
    if (isHosted) return 'Create a new revision to enable experiments';

    return 'Current server version does not support experiments. Upgrade to the latest version.';
  })();
  return (
    <Tooltip title={disabledMessage}>
      <Button
        variant="outlined"
        color="primary"
        onClick={onClick}
        size="md"
        disabled={!!disabledMessage}
        className="min-w-0 whitespace-nowrap "
      >
        Run experiment
      </Button>
    </Tooltip>
  );
};

export const ExperimentModalAndButton = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsDatasetId = searchParams.get('datasetId') ?? undefined;
  const { createToast } = useToast();
  const organizationId = useOrganizationId();

  const [experimentModalOpen, setExperimentModalOpen] = useState(
    searchParamsDatasetId != null
  );

  const assistantId = useAssistantStore((state) => state.activeAssistantId);
  const { data: assistant } = useGraphAssistant(assistantId);

  const [selectedDatasetId, _setSelectedDatasetId] = useState<
    string | undefined
  >(searchParamsDatasetId);
  const [selectedDatasetSplits, setSelectedDatasetSplits] = useState<string[]>(
    []
  );

  const setSelectedDatasetId = (datasetId?: string) => {
    _setSelectedDatasetId(datasetId);
    if (datasetId) {
      setSearchParams((prev) => {
        prev.set('datasetId', datasetId);
        return prev;
      });
    } else {
      setSearchParams((prev) => {
        prev.delete('datasetId');
        return prev;
      });
    }
  };

  const [experimentId, setExperimentId] = useState<string | undefined>();

  const [experimentName, setExperimentName] = useState(EXPERIMENT_PREFIX);
  useEffect(() => {
    const getAssistantDisplayName = () => {
      if (!assistant) return;
      if (checkIsSystemAssistant(assistant)) {
        return assistant.graph_id;
      }
      return assistant.name;
    };
    if (assistant) {
      setExperimentName(`${EXPERIMENT_PREFIX}::${getAssistantDisplayName()}`);
    }
  }, [assistant, setExperimentName]);

  const { data: examples } = useExamples(
    selectedDatasetId
      ? {
          dataset: selectedDatasetId,
          splits:
            selectedDatasetSplits.length > 0
              ? selectedDatasetSplits
              : undefined,
        }
      : null,
    undefined,
    { keepPreviousData: false }
  );

  const { isCompatible, missingFields } = useDatasetIsCompatibleWithSchema({
    datasetId: selectedDatasetId,
    splits: selectedDatasetSplits,
  });
  const { runInputs, creatingExperiment, progress } = useRunStudioExperiment({
    setExperimentId,
    setExperimentModalOpen,
  });

  const resetDatasetState = () => {
    setSelectedDatasetId(undefined);
    setSelectedDatasetSplits([]);
  };

  const handleExperimentButtonClick = () => {
    setExperimentModalOpen(true);
    resetDatasetState();
    setSearchParams((prev) => {
      prev.set('datasetId', '');
      return prev;
    });
  };
  const onClose = () => {
    setExperimentModalOpen(false);
    resetDatasetState();
  };

  const { success, failed, total } = progress;
  const completed = success + failed;
  const numInProgress = total - completed;

  const handleConfirm = async () => {
    if (
      !selectedDatasetId ||
      !examples?.length ||
      !experimentName ||
      !assistantId ||
      !isCompatible
    )
      return;
    try {
      const experiment = await runInputs({
        assistantId,
        examples,
        selectedDatasetId,
        experimentName,
      });
      setExperimentId(experiment.id);
    } catch (error) {
      console.error(error);
      createToast({
        title: 'Error',
        description: 'Failed to run experiment. Please try again.',
        type: 'error',
      });
    }
  };

  if (!assistant) return null;

  return (
    <>
      {!experimentId ? (
        <ExperimentButton onClick={handleExperimentButtonClick} />
      ) : (
        <div className="flex flex-row items-center gap-1">
          <div className="flex items-center gap-2 rounded-md border border-secondary bg-tertiary px-2 py-1 text-tertiary">
            <div className="relative flex h-5 items-center justify-center">
              <Tooltip
                title={
                  <div className="flex flex-col gap-1 text-xs font-medium">
                    {!!numInProgress && (
                      <span>{numInProgress} in progress</span>
                    )}
                    {!!success && <span>{success} succeeded</span>}
                    {!!failed && <span>{failed} failed</span>}
                  </div>
                }
              >
                <div className="flex items-center justify-center">
                  <CircularProgress
                    segments={[
                      {
                        color: 'var(--green-600)',
                        percentage: success / total,
                      },
                      {
                        color: 'var(--red-600)',
                        percentage: failed / total,
                      },
                      {
                        color: 'var(--orange-500)',
                        percentage: numInProgress / total,
                      },
                    ]}
                    size={20}
                    strokeWidth={3}
                    className="relative"
                  />
                </div>
              </Tooltip>
            </div>

            {completed < total ? (
              <span className="text-xs font-medium">
                Running Experiment {completed}/{total}
              </span>
            ) : (
              <span className="text-xs font-medium">Experiment completed</span>
            )}
            <Tooltip title="View experiment">
              <Link
                className="flex flex-row items-center gap-1 rounded-md p-0.5 text-sm text-primary underline hover:bg-secondary"
                target="_blank"
                to={`/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${selectedDatasetId}/${appComparePath}?selectedSessions=${experimentId}&textDisplayMode=${TextDisplayMode.FULL}`}
              >
                <ArrowNarrowUpRightIcon className="size-4" />
              </Link>
            </Tooltip>
          </div>
          <ExperimentButton onClick={handleExperimentButtonClick} />
        </div>
      )}
      {experimentModalOpen && (
        <ExperimentModal
          onClose={onClose}
          selectedDatasetId={selectedDatasetId}
          selectedDatasetSplits={selectedDatasetSplits}
          setSelectedDatasetId={setSelectedDatasetId}
          setSelectedDatasetSplits={setSelectedDatasetSplits}
          handleConfirm={handleConfirm}
          creatingExperiment={creatingExperiment}
          examples={examples}
          isCompatible={isCompatible}
          missingFields={missingFields}
        />
      )}
    </>
  );
};

const ExperimentModal = ({
  onClose,
  selectedDatasetId,
  selectedDatasetSplits,
  setSelectedDatasetId,
  setSelectedDatasetSplits,
  handleConfirm,
  creatingExperiment,
  examples,
  isCompatible,
  missingFields,
}: {
  onClose: () => void;
  selectedDatasetId: string | undefined;
  selectedDatasetSplits: string[];
  setSelectedDatasetId: (datasetId?: string) => void;
  setSelectedDatasetSplits: (splits: string[]) => void;
  handleConfirm: () => void;
  creatingExperiment: boolean;
  examples: ExampleSchema[] | undefined;
  isCompatible: boolean;
  missingFields: string[];
}) => {
  const disabledMessage = (() => {
    if (!selectedDatasetId) return 'Select a dataset';
    if (missingFields.length)
      return `Dataset is missing required fields: ${missingFields.join(', ')}`;
    if (!isCompatible) return 'Dataset is not compatible with graph schema';
    if (!examples?.length) return 'Dataset has no examples';
    return undefined;
  })();

  return (
    <Modal open={true} onClose={onClose}>
      <ModalDialog
        sx={{
          width: '550px',
          maxHeight: '5000px',
        }}
      >
        <div className="flex h-full flex-col gap-5">
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-col gap-2">
              <div className="flex flex-row items-center gap-1">
                <span className="text-md font-semibold">Run experiment</span>
                <a
                  href="https://docs.langchain.com/langgraph-platform/run-evals-studio"
                  target="_blank"
                >
                  <IconButton
                    icon={File05Icon}
                    label="Docs"
                    tooltipProps={{
                      side: 'right',
                    }}
                    variant="outlined"
                    color="secondary"
                  />
                </a>
              </div>
              <span className="text-sm text-secondary">
                Test your assistant over a dataset and run configured evaluators
                to measure your application's performance.
              </span>
            </div>
            <ModalClose />
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-sm text-secondary">Dataset</span>
              <DatasetAndSplitPicker
                // this prop is also used when clicking 'x' on the selected dataset
                onCreateNew={() => {
                  setSelectedDatasetId(undefined);
                }}
                hideCreateNew={true}
                datasetId={selectedDatasetId}
                isCreatingNewDataset={false}
                setDatasetId={setSelectedDatasetId}
                splits={selectedDatasetSplits}
                setSplits={setSelectedDatasetSplits}
              />
              <span
                className={cn(
                  'text-xs text-tertiary',
                  !examples?.length && 'invisible'
                )}
              >
                {xCount('example', examples?.length ?? 0)} selected
              </span>
            </div>
          </div>
          <div className="mt-auto flex flex-row justify-end gap-2">
            <Button
              onClick={onClose}
              variant="outlined"
              color="secondary"
              size="md"
            >
              Cancel
            </Button>
            <Tooltip title={disabledMessage} disabled={false}>
              <Button
                onClick={handleConfirm}
                disabled={!!disabledMessage}
                size="md"
                loading={creatingExperiment}
              >
                Start
              </Button>
            </Tooltip>
          </div>
        </div>
      </ModalDialog>
    </Modal>
  );
};
